import { getAppID } from "./payment";
import * as locationConfig from "./locationConfig";
import * as config from "./paymentConfig";

export default class SQPayment {
  APPLICATION_ID;
  LOCATION_ID;
  country;
  location;

  payments;
  card;
  googlePay;
  applePay;

  cardContainer;

  lineItems;
  totalAmount;

  // Values for verification details
  currencyCode;
  intent;
  addressLines;
  familyName;
  givenName;
  email;
  phone;
  region;
  city;

  // Extras
  occasion;

  constructor(
    params = {
      country: null,
      location: null,
      env: "prod",
      cardContainer: "#card-container",
    }
  ) {
    // Initialize options
    this.country = params.country;
    this.location = params.location;

    // const env = params.env;
    const env = "prod";
    // const env = "dev";

    // Search the Application ID and the Location ID
    this.APPLICATION_ID = getAppID(this.location, this.country, env);
    if (env.toLowerCase() === "prod") {
      this.LOCATION_ID = config.locationID[this.location.toLowerCase()];
    } else {
      if (params.country.toLowerCase() === "ca") {
        this.LOCATION_ID = config.locationID["default_ca"];
      }
      if (params.country.toLowerCase() === "us") {
        this.LOCATION_ID = config.locationID["default_us"];
      }
    }

    this.cardContainer = params.cardContainer;
  }

  async init(parentEl = null) {
    this.payments = Square.payments(this.APPLICATION_ID, this.LOCATION_ID);
    this.card = await this.payments.card({
      style: {
        ".input-container": {
          borderWidth: "2px",
          borderColor: "rgba(156,163,175,1)",
        },
      },
    });

    if (parentEl === null) {
      await this.card.attach(`${this.cardContainer}`);
    } else {
      const el = document
        .getElementById(parentEl)
        .querySelector(`${this.cardContainer}`);
      await this.card.attach(el);
    }
  }

  async tokenize(paymentMethod) {
    const tokenResult = await paymentMethod.tokenize();
    if (tokenResult.status === "OK") {
      return tokenResult.token;
    } else {
      let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
      if (tokenResult.errors) {
        errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
      }
      return tokenResult;
    }
  }

  async paymentEventHandler(event = null, paymentMethod) {
    if (event !== null) {
      event.preventDefault();
    }

    try {
      // const result = await this.card.tokenize();
      let token;
      if (paymentMethod === "card") {
        token = await this.tokenize(this.card);
      }
      if (paymentMethod === "google-pay") {
        token = await this.tokenize(this.googlePay);
      }
      if (paymentMethod === "apple-pay") {
        token = await this.tokenize(this.applePay);
      }
      // const paymentResults = await createPayment(to);
      return token;

      // if (result.status === 'OK') {
      //     return result;
      // }
      // return result
    } catch (e) {
      return {
        errors: [e],
      };
    }
  }

  getPayments() {
    return this.payments;
  }

  getCard() {
    return this.card;
  }

  setCountryCode() { }

  getCountryCode() {
    let code = "US";
    return code;
  }

  getCountry() {
    if (this.country === "uk") return "GB";
    return this.country.toUpperCase();
  }

  getCurrencyCode() {
    if (this.country.toLowerCase() === "us") {
      return "USD";
    } else if (this.country.toLowerCase() === "ca") {
      return "USD";
    } else {
      return "GBP";
    }
  }

  setLineItems(item = {}) {
    this.lineItems.push(item);
  }

  getLineItems() {
    return this.lineItems;
  }

  setTotalAmount(totalAmount) {
    this.totalAmount = totalAmount;
  }

  getTotalAmount() {
    return this.totalAmount;
  }

  buildPaymentRequest() {
    return this.payments.paymentRequest({
      countryCode: this.country.toUpperCase(),
      currencyCode: this.getCurrencyCode(),
      total: {
        amount: this.getTotalAmount(),
        label: "Total",
      },
    });
  }

  async initGooglePay(parentEl = null, sectionuid = null) {
    const paymentRequest = this.buildPaymentRequest();
    if (typeof this.googlePay !== "undefined") await this.googlePay.destroy();

    this.googlePay = await this.payments.googlePay(paymentRequest);
    let googlePayId =
      sectionuid !== null
        ? "#google-pay-button-" + sectionuid
        : "#google-pay-button";
    if (parentEl === null) {
      await this.googlePay.attach(`${googlePayId}`);
    } else {
      // const el = document.getElementById(parentEl).querySelector('#google-pay-button');
      const el = document
        .getElementById(parentEl)
        .querySelector(`${googlePayId}`);
      await this.googlePay.attach(el);
    }
  }

  async initApplePay() {
    const paymentRequest = this.buildPaymentRequest();
    if (typeof this.applePay !== "undefined") await this.applePay.destroy();
    this.applePay = await this.payments.applePay(paymentRequest);
    await this.applePay;
  }

  setIntent(intent) {
    this.intent = intent;
  }

  getIntent() {
    if (this.intent === null || typeof this.intent === "undefined")
      return "CHARGE";
    return this.intent;
  }

  setAddressLines(addressLine) {
    this.addressLines.push(addressLine);
  }

  getAddressLines() {
    return this.addressLines;
  }

  setFamilyName(familyName) {
    this.familyName = familyName;
  }

  getFamilyName() {
    return this.familyName;
  }

  setGivenName(givenName) {
    this.givenName = givenName;
  }

  getGivenName() {
    return this.givenName;
  }

  setEmail(email) {
    this.email = email;
  }

  getEmail() {
    return this.email;
  }

  setPhone(phone) {
    this.phone = phone;
  }

  getPhone() {
    return this.phone;
  }

  setRegion(region) {
    this.region = region;
  }

  getRegion() {
    return this.region;
  }

  setCity(city) {
    this.city = city;
  }

  getCity() {
    return this.city;
  }

  setOccasion(occasion) {
    this.occasion = occasion;
  }

  getOccasion() {
    return this.occasion;
  }

  // Verification details
  verificationDetails() {
    return {
      amount: this.getTotalAmount(),
      currencyCode: this.getCurrencyCode(), // Currency code depending of the country
      intent: this.getIntent(),
      billingContact: {
        addressLines: this.getAddressLines(),
        familyName: this.getFamilyName(),
        givenName: this.getGivenName(),
        email: this.getEmail(),
        country: this.getCountry(),
        phone: this.getPhone(),
        region: this.getRegion(),
        city: this.getCity(),
      },
    };
  }

  async destroy() {
    return new Promise((resolve) => {
      let cardDestroy;
      let googlePayDestroy;
      let applePayDestroy;

      if (typeof this.googlePay !== "undefined") {
        googlePayDestroy = this.googlePay.destroy().then((response) => { });
      }

      if (typeof this.card !== "undefined") {
        cardDestroy = this.card.destroy().then((response) => {
          resolve(cardDestroy);
        });
      }
    });
  }

  getGooglePay() {
    return this.googlePay;
  }

  async destroyGooglePay() {
    this.googlePay.destroy().then((response) => {
      console.log("Google pay response: ", response);
    });
  }
}
